import { User, Key, Users, Globe, Home, Calendar } from 'lucide-react';
import CustomInput from '../../../../../../../components/forms/CustomInput';
import CustomSelect from '../../../../../../../components/forms/CustomSelect';
import CustomDatePicker from '../../../../../../../components/forms/CustomDatePicker';

const GuestInfoSection = ({
  formData,
  handleInputChange,
  bookingId,
  parseStandardDate
}) => {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-6 md:p-8 shadow-sm" id="guest-info-section">
      <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
        <div className="w-7 h-7 sm:w-10 sm:h-10 shrink-0 bg-teal-50 text-primary rounded-lg sm:rounded-xl flex items-center justify-center shadow-inner">
          <User size={14} className="sm:w-5 sm:h-5" />
        </div>
        <div>
          <h3 className="text-xs sm:text-base font-semibold sm:font-bold text-slate-800 leading-tight">Guest Information</h3>
          <p className="hidden sm:block text-xs text-slate-500 font-normal sm:font-medium mt-0.5">Add guest details and trip preferences</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <CustomInput
            label="Guest full name *"
            icon={User}
            value={formData.guest_information?.guest_name || ''}
            onChange={(e) => handleInputChange('guest_information.guest_name', e.target.value)}
            placeholder="Main Guest Name"
          />
        </div>
        <div>
          <CustomInput
            label="Voucher ID (5 chars, lowercase, alphanumeric)"
            icon={Key}
            value={bookingId || ''}
            disabled
            readOnly
            placeholder="e.g. a1b2c"
            maxLength={5}
            title="Custom Voucher ID is available in Enterprise plan"
            inputClassName="!bg-[#f4f7fa] !border-slate-200 !text-[#64748b] font-bold text-sm"
          />
        </div>
        <div>
          <CustomSelect
            label="Adults"
            value={formData.guest_information?.total_guests_adults ?? ''}
            onChange={(val) => handleInputChange('guest_information.total_guests_adults', val === '' ? '' : parseInt(val))}
            options={[...Array(16).keys()].map(num => ({ label: String(num), value: num }))}
            icon={Users}
            placeholder="0"
          />
        </div>
        <div>
          <CustomSelect
            label="Children"
            value={formData.guest_information?.total_guests_children ?? ''}
            onChange={(val) => handleInputChange('guest_information.total_guests_children', val === '' ? '' : parseInt(val))}
            options={[...Array(16).keys()].map(num => ({ label: String(num), value: num }))}
            icon={Users}
            placeholder="0"
          />
        </div>
        <div>
          <CustomSelect
            label="Preferred language"
            value={formData.preferred_language || 'English'}
            onChange={(val) => handleInputChange('preferred_language', val)}
            options={['English', 'Hindi', 'Marathi', 'Telugu', 'Tamil', 'Gujarati']}
            icon={Globe}
            placeholder="Select language"
          />
        </div>
        <div>
          <CustomInput
            label="Rooms booked"
            icon={Home}
            type="number"
            min="0"
            value={formData.guest_information?.total_rooms_booked || ''}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '' || Number(val) >= 0) {
                handleInputChange('guest_information.total_rooms_booked', val);
              }
            }}
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-700 tracking-wide mb-1.5">
            Start date <span className="text-rose-500 ml-0.5">*</span>
          </label>
          <CustomDatePicker
            value={formData.tour_info?.start_date}
            onChange={(val) => handleInputChange('tour_info.start_date', val)}
            minDate={new Date()}
            leftIcon={<Calendar size={16} />}
            inputClassName="border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary rounded-xl py-2.5 text-sm font-semibold text-slate-800 transition-all outline-none shadow-sm"
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-700 tracking-wide mb-1.5">
            End date <span className="text-rose-500 ml-0.5">*</span>
          </label>
          <CustomDatePicker
            value={formData.tour_info?.end_date}
            onChange={(val) => handleInputChange('tour_info.end_date', val)}
            minDate={(() => {
              const startD = parseStandardDate(formData.tour_info?.start_date);
              if (startD) {
                const nextD = new Date(startD);
                nextD.setDate(nextD.getDate() + 1);
                return nextD;
              }
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              return tomorrow;
            })()}
            leftIcon={<Calendar size={16} />}
            inputClassName="border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary rounded-xl py-2.5 text-sm font-semibold text-slate-800 transition-all outline-none shadow-sm"
            className="w-full"
          />
        </div>
      </div>
    </div>
  );
};

export default GuestInfoSection;
